import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import { useEffect, useState, useCallback } from "react";
import { toast } from "sonner";
import { Loader2, LogOut, Phone, Calendar as CalIcon, Trash2, ArrowLeft } from "lucide-react";
import { format } from "date-fns";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { hasAdminAccess } from "@/lib/admin-access";
import { buildWhatsAppUrl } from "@/lib/phone";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

type Signup = {
  id: string;
  name: string;
  phone: string;
  category: "fresher" | "visa_experience" | "refusal";
  visa_type: string | null;
  interview_date: string | null;
  status: "new" | "contacted" | "added" | "rejected";
  notes: string | null;
  created_at: string;
};

const CATEGORY_LABEL: Record<Signup["category"], string> = {
  fresher: "Fresher",
  visa_experience: "Visa Experience",
  refusal: "Refusal",
};

const STATUS_COLOR: Record<Signup["status"], string> = {
  new: "bg-primary/10 text-primary border-primary/20",
  contacted: "bg-amber-100 text-amber-900 border-amber-200",
  added: "bg-emerald-100 text-emerald-900 border-emerald-200",
  rejected: "bg-muted text-muted-foreground border-border",
};

export const Route = createFileRoute("/admin/signups")({
  head: () => ({
    meta: [{ title: "Group signups - Admin" }, { name: "robots", content: "noindex, nofollow" }],
  }),
  component: AdminSignupsPage,
});

function AdminSignupsPage() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [authorized, setAuthorized] = useState(false);
  const [signups, setSignups] = useState<Signup[]>([]);
  const [filter, setFilter] = useState<"all" | Signup["status"]>("new");

  const load = useCallback(async () => {
    const { data, error } = await supabase
      .from("group_signups")
      .select("*")
      .order("created_at", { ascending: false });
    if (error) {
      toast.error("Couldn't load signups");
      return;
    }
    setSignups((data ?? []) as Signup[]);
  }, []);

  useEffect(() => {
    let active = true;
    (async () => {
      const { data: sessionData } = await supabase.auth.getSession();
      if (!sessionData.session) {
        navigate({ to: "/auth" });
        return;
      }
      if (!active) return;
      if (!(await hasAdminAccess(sessionData.session))) {
        toast.error("This account is not an admin yet.");
        setAuthorized(false);
        setLoading(false);
        return;
      }
      setAuthorized(true);
      await load();
      setLoading(false);
    })();
    return () => {
      active = false;
    };
  }, [navigate, load]);

  const updateStatus = async (id: string, status: Signup["status"]) => {
    const prev = signups;
    setSignups((s) => s.map((row) => (row.id === id ? { ...row, status } : row)));
    const { error } = await supabase.from("group_signups").update({ status }).eq("id", id);
    if (error) {
      setSignups(prev);
      toast.error("Couldn't update status");
    } else {
      toast.success("Status updated");
    }
  };

  const deleteSignup = async (s: Signup) => {
    if (!window.confirm(`Revoke group signup from ${s.name}?\nThis cannot be undone.`)) return;
    const prev = signups;
    setSignups((rows) => rows.filter((row) => row.id !== s.id));
    const { error } = await supabase.from("group_signups").delete().eq("id", s.id);
    if (error) {
      setSignups(prev);
      toast.error("Couldn't revoke signup");
    } else {
      toast.success("Signup revoked");
    }
  };

  const handleSignOut = async () => {
    await supabase.auth.signOut();
    navigate({ to: "/auth" });
  };

  const filtered = signups.filter((s) => filter === "all" || s.status === filter);
  const counts = {
    new: signups.filter((s) => s.status === "new").length,
    contacted: signups.filter((s) => s.status === "contacted").length,
    added: signups.filter((s) => s.status === "added").length,
    rejected: signups.filter((s) => s.status === "rejected").length,
  };

  if (loading) {
    return (
      <div className="min-h-[60vh] grid place-items-center">
        <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
      </div>
    );
  }

  if (!authorized) {
    return (
      <div className="min-h-[60vh] grid place-items-center px-5">
        <div className="max-w-md text-center">
          <h1 className="font-display text-2xl">Not authorized</h1>
          <p className="mt-2 text-muted-foreground">Your account doesn't have admin access yet.</p>
          <div className="mt-4 flex justify-center gap-3">
            <Button variant="outline" onClick={handleSignOut}>
              Sign out
            </Button>
            <Link to="/" className="inline-flex items-center px-4 py-2 rounded-md text-sm">
              Go home
            </Link>
          </div>
        </div>
      </div>
    );
  }

  return (
    <section className="mx-auto max-w-6xl px-5 py-12">
      <Link
        to="/admin"
        className="inline-flex items-center gap-1 text-sm text-muted-foreground hover:text-primary mb-4"
      >
        <ArrowLeft className="h-3.5 w-3.5" /> Back to admin
      </Link>
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <p className="text-sm font-medium text-primary uppercase tracking-wider">Admin</p>
          <h1 className="mt-1 font-display text-4xl font-semibold">Group signups</h1>
          <p className="mt-1 text-sm text-muted-foreground">
            {counts.new} new · {counts.contacted} contacted · {counts.added} added ·{" "}
            {counts.rejected} rejected
          </p>
        </div>
        <div className="flex items-center gap-3">
          <Select value={filter} onValueChange={(v) => setFilter(v as typeof filter)}>
            <SelectTrigger className="w-[160px]">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All</SelectItem>
              <SelectItem value="new">New</SelectItem>
              <SelectItem value="contacted">Contacted</SelectItem>
              <SelectItem value="added">Added</SelectItem>
              <SelectItem value="rejected">Rejected</SelectItem>
            </SelectContent>
          </Select>
          <Button variant="outline" onClick={handleSignOut} className="gap-2">
            <LogOut className="h-4 w-4" /> Sign out
          </Button>
        </div>
      </div>

      <div className="mt-8 grid gap-3">
        {filtered.length === 0 && (
          <div className="rounded-2xl border border-dashed border-border p-10 text-center text-muted-foreground">
            No signups in this filter yet.
          </div>
        )}
        {filtered.map((s) => (
          <article
            key={s.id}
            className="rounded-2xl border border-border bg-card p-5 shadow-soft/40"
          >
            <div className="flex flex-wrap items-start justify-between gap-3">
              <div className="min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                  <h3 className="font-display text-lg font-semibold">{s.name}</h3>
                  <Badge variant="outline" className={STATUS_COLOR[s.status]}>
                    {s.status}
                  </Badge>
                  <Badge variant="secondary">{CATEGORY_LABEL[s.category]}</Badge>
                  {s.visa_type && <Badge variant="outline">{s.visa_type}</Badge>}
                </div>
                <div className="mt-2 flex flex-wrap gap-x-4 gap-y-1 text-sm text-muted-foreground">
                  <a
                    href={buildWhatsAppUrl(s.phone, `Hi ${s.name || "there"}! This is the WeLivdIt team.`)}
                    target="_blank"
                    rel="noreferrer"
                    className="inline-flex items-center gap-1 text-primary hover:underline"
                  >
                    <Phone className="h-3.5 w-3.5" /> {s.phone}
                  </a>
                  {s.interview_date && (
                    <span className="inline-flex items-center gap-1">
                      <CalIcon className="h-3.5 w-3.5" /> Interview{" "}
                      {format(new Date(s.interview_date), "PP")}
                    </span>
                  )}
                  <span>Submitted {format(new Date(s.created_at), "PP p")}</span>
                </div>
              </div>
              <div className="flex flex-col items-end gap-2">
                <Select
                  value={s.status}
                  onValueChange={(v) => updateStatus(s.id, v as Signup["status"])}
                >
                  <SelectTrigger className="w-[140px]">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="new">New</SelectItem>
                    <SelectItem value="contacted">Contacted</SelectItem>
                    <SelectItem value="added">Added</SelectItem>
                    <SelectItem value="rejected">Rejected</SelectItem>
                  </SelectContent>
                </Select>
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => deleteSignup(s)}
                  className="gap-1.5 text-destructive hover:text-destructive"
                >
                  <Trash2 className="h-3.5 w-3.5" /> Revoke
                </Button>
              </div>
            </div>
          </article>
        ))}
      </div>
    </section>
  );
}
